import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { fitnessAPI, vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { FitnessCertificate, PaymentMode, Vehicle } from '../../types/index';
import { Button } from '../Button';

interface AddFitnessModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  fitness?: FitnessCertificate
  onSuccess?: () => void
}

type FitnessClass = 'commercial' | 'private'
type FitnessResult = 'pass' | 'fail' | 'conditional'

const fitnessClasses: FitnessClass[] = ['commercial', 'private']
const fitnessResults: FitnessResult[] = ['pass', 'fail', 'conditional']
const paymentModes: PaymentMode[] = ['online', 'cash', 'dd', 'cheque', 'neft']

export default function AddFitnessModal({
  open,
  onClose,
  customerId,
  fitness,
  onSuccess,
}: AddFitnessModalProps) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    certificate_number: '',
    fitness_class: '' as FitnessClass | '',
    inspection_date: '',
    expiry_date: '',
    result: '' as FitnessResult | '',
    conditional_remarks: '',
    inspecting_authority: '',
    inspector_name: '',
    inspector_badge: '',
    fee_paid: '',
    payment_mode: '' as PaymentMode | '',
    receipt_number: '',
    notes: '',
  })

  // Fetch vehicles for the customer
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId),
    enabled: open,
  })

  useEffect(() => {
    if (fitness) {
      setFormData({
        vehicle_id: fitness.vehicle_id || '',
        certificate_number: fitness.certificate_number || '',
        fitness_class: fitness.fitness_class || '',
        inspection_date: fitness.inspection_date || '',
        expiry_date: fitness.expiry_date || '',
        result: fitness.result || '',
        conditional_remarks: fitness.conditional_remarks || '',
        inspecting_authority: fitness.inspecting_authority || '',
        inspector_name: fitness.inspector_name || '',
        inspector_badge: fitness.inspector_badge || '',
        fee_paid: fitness.fee_paid || '',
        payment_mode: fitness.payment_mode || '',
        receipt_number: fitness.receipt_number || '',
        notes: fitness.notes || '',
      })
    } else {
      setFormData({
        vehicle_id: '',
        certificate_number: '',
        fitness_class: '',
        inspection_date: '',
        expiry_date: '',
        result: '',
        conditional_remarks: '',
        inspecting_authority: '',
        inspector_name: '',
        inspector_badge: '',
        fee_paid: '',
        payment_mode: '',
        receipt_number: '',
        notes: '',
      })
    }
  }, [fitness, open])

  const createMutation = useMutation({
    mutationFn: () =>
      fitnessAPI.create(customerId, {
        ...formData,
        fitness_class: (formData.fitness_class || null) as any,
        result: (formData.result || null) as any,
        payment_mode: (formData.payment_mode || null) as any,
        fee_paid: formData.fee_paid ? parseFloat(formData.fee_paid) : null,
        conditional_remarks: formData.conditional_remarks || null,
        inspecting_authority: formData.inspecting_authority || null,
        inspector_name: formData.inspector_name || null,
        inspector_badge: formData.inspector_badge || null,
        receipt_number: formData.receipt_number || null,
        notes: formData.notes || null,
      } as any),
    onSuccess: () => {
      toast.success('Fitness certificate created')
      queryClient.invalidateQueries({ queryKey: ['fitness'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to create fitness certificate',
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      fitnessAPI.update(fitness!.id, {
        ...formData,
        fitness_class: (formData.fitness_class || null) as any,
        result: (formData.result || null) as any,
        payment_mode: (formData.payment_mode || null) as any,
        fee_paid: formData.fee_paid ? parseFloat(formData.fee_paid) : null,
        conditional_remarks: formData.conditional_remarks || null,
        inspecting_authority: formData.inspecting_authority || null,
        inspector_name: formData.inspector_name || null,
        inspector_badge: formData.inspector_badge || null,
        receipt_number: formData.receipt_number || null,
        notes: formData.notes || null,
      } as any),
    onSuccess: () => {
      toast.success('Fitness certificate updated')
      queryClient.invalidateQueries({ queryKey: ['fitness'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to update fitness certificate',
      )
    },
  })

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    isLoadingVehicles
  const isEdit = !!fitness

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !formData.vehicle_id.trim() ||
      !formData.certificate_number.trim() ||
      !formData.inspection_date
    ) {
      toast.error(
        'Vehicle, certificate number, and inspection date are required',
      )
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  const vehicles = vehiclesData?.data?.vehicles || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Fitness Certificate' : 'Add Fitness Certificate'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle *
              </label>
              <select
                required
                disabled={isLoading || isEdit}
                value={formData.vehicle_id}
                onChange={(e) =>
                  setFormData({ ...formData, vehicle_id: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select vehicle</option>
                {Array.isArray(vehicles) && vehicles.map((vehicle: Vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Certificate Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.certificate_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    certificate_number: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="FC-2024-001"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fitness Class
              </label>
              <select
                disabled={isLoading}
                value={formData.fitness_class}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fitness_class: e.target.value as FitnessClass | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select fitness class</option>
                {fitnessClasses.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls.charAt(0).toUpperCase() + cls.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Inspection Date *
              </label>
              <input
                type="date"
                required
                disabled={isLoading}
                value={formData.inspection_date}
                onChange={(e) =>
                  setFormData({ ...formData, inspection_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.expiry_date}
                onChange={(e) =>
                  setFormData({ ...formData, expiry_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Result
              </label>
              <select
                disabled={isLoading}
                value={formData.result}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    result: e.target.value as FitnessResult | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select result</option>
                {fitnessResults.map((result) => (
                  <option key={result} value={result}>
                    {result.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 4 - Conditional Remarks (shown only when result is conditional) */}
          {formData.result === 'conditional' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Conditional Remarks
              </label>
              <textarea
                disabled={isLoading}
                value={formData.conditional_remarks}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    conditional_remarks: e.target.value,
                  })
                }
                rows={3}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Enter remarks for conditional approval"
              />
            </div>
          )}

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Inspecting Authority
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.inspecting_authority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    inspecting_authority: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Authority Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Inspector Name
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.inspector_name}
                onChange={(e) =>
                  setFormData({ ...formData, inspector_name: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Inspector Name"
              />
            </div>
          </div>

          {/* Row 6 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Inspector Badge
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.inspector_badge}
                onChange={(e) =>
                  setFormData({ ...formData, inspector_badge: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Badge Number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fee Paid
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.fee_paid}
                onChange={(e) =>
                  setFormData({ ...formData, fee_paid: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="500"
              />
            </div>
          </div>

          {/* Row 7 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Mode
              </label>
              <select
                disabled={isLoading}
                value={formData.payment_mode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_mode: e.target.value as PaymentMode | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select payment mode</option>
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Receipt Number
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.receipt_number}
                onChange={(e) =>
                  setFormData({ ...formData, receipt_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="REC-001"
              />
            </div>
          </div>

          {/* Row 8 - Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              disabled={isLoading}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="Any additional notes"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Certificate' : 'Add Certificate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
